<template>
  <div class="step2">
    <div class="donar-info">
      <h4>Personal Info</h4>
      <form>
        <input
          type="text"
          :placeholder="trans('first_name')"
          v-model="store.billing_fields.first_name"
          :disabled="store.loading"
          required
        />
        <input
          type="text"
          :placeholder="trans('last_name')"
          v-model="store.billing_fields.last_name"
          :disabled="store.loading"
          required
        />
        <input
          type="email"
          :placeholder="trans('email')"
          v-model="store.billing_fields.email"
          :readonly="is_logged_in && email"
          :disabled="store.loading"
          required
        />
        <input
          type="tel"
          :placeholder="trans('phone')"
          v-model="store.billing_fields.phone"
          :disabled="store.loading"
          onkeyup="this.value=this.value.replace(/[^\d]/,'')"
        />
        <input
          type="text"
          :placeholder="trans('address')"
          v-model="store.billing_fields.address"
          :disabled="store.loading"
        />
        <div v-if="show_anonymous_donation" class="wpcm-checkbox-field">
          <el-checkbox
            v-model="store.billing_fields.anonymous_donation"
            :disabled="store.loading"
          >
            {{ trans('anonymous_donation') }}
          </el-checkbox>
        </div>
        <div
          v-if="show_fee_recovery && fee_recovery_optin_mode === 'donor'"
          class="wpcm-checkbox-field wpcm-fee-recovery-field"
        >
          <el-checkbox
            v-model="store.extras.fee_recovery_optin"
            :disabled="store.loading"
          >
            {{ feeRecoveryMessage }}
          </el-checkbox>
          <div
            v-if="fee_recovery_show_breakdown && store.extras.fee_recovery_optin"
            class="wpcm-fee-recovery-breakdown"
          >
            {{ feeRecoveryBreakdown }}
          </div>
        </div>
        <div
          v-else-if="show_fee_recovery && fee_recovery_optin_mode === 'forced'"
          class="wpcm-checkbox-field wpcm-fee-recovery-field wpcm-fee-recovery-forced"
        >
          <span>{{ feeRecoveryMessage }}</span>
          <div v-if="fee_recovery_show_breakdown" class="wpcm-fee-recovery-breakdown">
            {{ feeRecoveryBreakdown }}
          </div>
        </div>
        <div v-if="enable_test_card_information && trans('test_card_information')" class="wpcm-test-card-info">
          <p class="wpcm-test-card-text" v-html="trans('test_card_information')"></p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch } from 'vue';
import { useDonationFormsStore } from '../../store';

const store = useDonationFormsStore();
const props = defineProps([
  'is_logged_in', 'email', 'show_anonymous_donation', 'enable_test_card_information', 'strings',
  'show_fee_recovery', 'fee_recovery_optin_mode', 'fee_recovery_optin_message',
  'fee_recovery_show_breakdown', 'fee_recovery_mode', 'fee_recovery_global',
  'fee_recovery_per_gateway', 'fee_recovery_currency'
]);

const setValue = (key, val) => {
  store[key] = val;
};
const setBillingValue = (key, val) => {
  store.billing_fields[key] = (val.target as HTMLInputElement).value;
};
const trans = (key) => {
  if(props.strings) {
    return props.strings[key]
  }
};

const feeRecoveryRate = computed(() => {
  const fallback = { percent: 0, flat: 0, max: 0 };
  if (props.fee_recovery_mode === 'per_gateway') {
    const map = props.fee_recovery_per_gateway || {};
    if (store.payment_method && map[store.payment_method]) {
      return map[store.payment_method];
    }
    const keys = Object.keys(map);
    if (keys.length > 0) {
      return map[keys[0]];
    }
  }
  return props.fee_recovery_global || fallback;
});

const feeRecoveryAmount = computed(() => {
  const base = parseFloat(String(store.amount)) || 0;
  if (base <= 0) return 0;
  const r: any = feeRecoveryRate.value || {};
  let fee = base * ((parseFloat(r.percent) || 0) / 100) + (parseFloat(r.flat) || 0);
  const max = parseFloat(r.max) || 0;
  if (max > 0 && fee > max) fee = max;
  return Math.max(0, Math.round(fee * 100) / 100);
});

const formatMoney = (n: number) => {
  const num = parseFloat(String(n)) || 0;
  return (props.fee_recovery_currency || '$') + num.toFixed(2);
};

const feeRecoveryMessage = computed(() => {
  const tpl = props.fee_recovery_optin_message || '';
  return tpl.replace('{fee_amount}', formatMoney(feeRecoveryAmount.value));
});

const feeRecoveryBreakdown = computed(() => {
  const donation = parseFloat(String(store.amount)) || 0;
  const opted = props.fee_recovery_optin_mode === 'forced' || !!store.extras.fee_recovery_optin;
  const fee = opted ? feeRecoveryAmount.value : 0;
  const total = donation + fee;
  const tpl = (props.strings && props.strings.fee_recovery_breakdown)
    || 'Donation: {donation}  +  Fee: {fee}  =  Total: {total}';
  return tpl
    .replace('{donation}', formatMoney(donation))
    .replace('{fee}', formatMoney(fee))
    .replace('{total}', formatMoney(total));
});

watch(
  [
    () => feeRecoveryAmount.value,
    () => store.amount,
    () => store.extras.fee_recovery_optin,
    () => props.fee_recovery_optin_mode,
    () => props.show_fee_recovery,
  ],
  () => {
    const opted = props.show_fee_recovery && (
      props.fee_recovery_optin_mode === 'forced' || !!store.extras.fee_recovery_optin
    );
    store.extras.fee_recovery_amount = opted ? feeRecoveryAmount.value : 0;
    store.extras.fee_recovery_base   = parseFloat(String(store.amount)) || 0;
  },
  { immediate: true }
);

onMounted(() => {
  if(props.is_logged_in && props.email) {
      store.billing_fields.email = props.email;
    }
    if (props.show_fee_recovery && props.fee_recovery_optin_mode === 'forced') {
      store.extras.fee_recovery_optin = true;
    }
});


</script>